// @flow
import type { Checker } from './validation/runtime-checks/index.js';
import { assert } from './assert.js';

// 🧪 Experimental checker. Don't use extensively.
//
// The general idea is we want to be able to specify constraints on the
// number of children in a clear manner. This is a very rough experiment
// for that which has a number of limitiations and hasn't been tested extensively.
//
// The most notable constraint is you can onyl express min/max constraints
// for single element types (doesn't support unions or other constructs).
//
// Expected usage:
//
// childrenArrayMatcher(
//   { min: 1, max: 1, elementType: t.elementType(LSNLessonIntroduction) },
//   { min: 1, max: Infinity, elementType: t.elementType(LSNIdeaSection) },
// ),
export function childrenArrayMatcher(
  ...childrenConstraints: Array<{|
    elementType: Checker,
    max: number,
    min: number,
  |}>
): Checker {
  assert(
    childrenConstraints.length > 0,
    'childrenArrayMatcher requires at least one constraint',
  );

  for (const constraint of childrenConstraints) {
    assert(constraint.min >= 0, 'min must be >= 0');
    // TODO this check is quite brittle, figure out a nicer way to check type
    assert(
      constraint.elementType.name === 'elementTypeChecker',
      'elementType only supports t.elementType',
    );
  }

  // NOTE its important that 'react' is not part of the `jsx-content` package
  // so we are just going to make our own basic type for ReactElement
  type ReactElement = { type: Function };

  function childrenArrayMatcherChecker(props, propName) {
    // TODO can we do a runtime check to ensure this type coercion is sound?
    let children: $ReadOnlyArray<ReactElement> = Array.isArray(props[propName])
      ? (props[propName]: any)
      : ([props[propName]]: any);

    let childIndex = 0;
    let constraintIndex = 0;
    constraints: for (
      ;
      constraintIndex < childrenConstraints.length;
      constraintIndex++
    ) {
      const { min, max, elementType } = childrenConstraints[constraintIndex];
      let checkCount = 0;
      while (checkCount < max) {
        // If we have run out of children, make sure that we have satisfied
        // all constraints
        if (childIndex >= children.length) {
          const remainingConstraints = childrenConstraints.slice(
            constraintIndex + 1,
          );
          if (
            checkCount >= min &&
            remainingConstraints.every((c) => c.min === 0)
          ) {
            // If the current constraint has been satisfied, and any subsequent constrains are optional; we're good.
            break constraints;
          } else {
            // Otherwise, not enough children have been provided to meet the constraints
            return getErrorMessage({
              suggestedFix: `More children need to be specified after <${
                children[children.length - 1].type.name
              }>`,
            });
          }
        }

        // $FlowIgnore doesn't want us leveraging the Objectness of arrays like this
        const result = elementType(children, childIndex);
        if (result !== null) {
          if (checkCount >= min) {
            // We have satisfied the minimum count requirement, so move onto
            // trying to validate this child against the next constraint
            continue constraints;
          }
          const expected = elementType.description;
          const childNumber = childIndex + 1;
          const _childDescription = childDescription(children[childIndex]);
          return getErrorMessage({
            suggestedFix: `Child #${childNumber} needs to be ${expected}; not ${_childDescription}`,
          });
        }

        checkCount += 1;
        childIndex += 1;
      }
    }

    if (childIndex < children.length) {
      // We do not allow dangling children that haven't matched against the constraints.
      const _childDescription = childDescription(children[childIndex]);
      return getErrorMessage({
        suggestedFix: `${_childDescription} at position #${childIndex} (and any subsequent components) do not match the constraints`,
      });
    }

    return null;
  }
  childrenArrayMatcherChecker.description = [
    'In this precise order:',
    ...childrenConstraints.map(
      (constraint) =>
        `At least ${constraint.min} and at most ${constraint.max} ${constraint.elementType.description}`,
    ),
  ].join('\n');

  function getErrorMessage({ suggestedFix }) {
    const constraintsList = childrenConstraints
      .map(
        (c) => `  - ${c.elementType.description} (${constraintDescription(c)})`,
      )
      .join('\n');

    return {
      typeDescription: childrenArrayMatcherChecker.description,
      reason: [
        `must specify components in the following order: `,
        constraintsList,
        ``,
        suggestedFix,
      ].join('\n'),
    };
  }

  // Provides some more readable shorthands for some common scenarios
  function constraintDescription(constraint) {
    const { min, max } = constraint;
    if (min === 0 && max === 1) return 'optional';
    if (min === 1 && max === 1) return 'exactly one';
    if (max === Infinity) return `${min} or more times`;
    return `between ${min} and ${max} times`;
  }

  return childrenArrayMatcherChecker;
}

function childDescription(child) {
  if (typeof child === 'string') return `"${child}"`;
  const componentName =
    child.type.__wrappedComponent !== undefined
      ? child.type.__wrappedComponent.name
      : child.type.name;
  return `<${componentName}>`;
}
